import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import { Wallet, Download, Upload, Banknote, Search, X, RotateCcw, Trash2 } from 'lucide-react';

const BusinessPayments = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : (outletContext?.canManagePayments ?? true);
  
  // Base URL de l'API selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    loadPayments();
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, projectId]);

  const loadPayments = async () => {
    try {
      let url = `${apiBase}/payments`;
      if (filter !== 'all') url += `?type=${filter}`;
      const res = await api.get(url);
      setPayments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await api.get(`${apiBase}/clients`);
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadInvoicesForClient = async (clientId) => {
    try {
      const res = await api.get(`${apiBase}/invoices?client_id=${clientId}&statut=envoyee,partiellement_payee`);
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ message: 'Supprimer ce paiement ?', type: 'danger' })) return;
    try {
      await api.delete(`${apiBase}/payments/${id}`);
      loadPayments();
    } catch (err) {
      console.error(err);
    }
  };

  // Stats
  const stats = {
    totalEncaissements: payments
      .filter(p => p.type === 'encaissement')
      .reduce((sum, p) => sum + parseFloat(p.montant || 0), 0),
    totalDecaissements: payments
      .filter(p => p.type === 'decaissement')
      .reduce((sum, p) => sum + parseFloat(p.montant || 0), 0),
    countEncaissements: payments.filter(p => p.type === 'encaissement').length,
    countDecaissements: payments.filter(p => p.type === 'decaissement').length
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  // Filtrage par recherche
  const filteredPayments = payments.filter(payment => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      payment.client_nom?.toLowerCase().includes(searchLower) ||
      payment.fournisseur?.toLowerCase().includes(searchLower) ||
      payment.reference?.toLowerCase().includes(searchLower) ||
      payment.numero_facture?.toLowerCase().includes(searchLower) ||
      payment.mode_paiement?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Paiements</h1>
          <p className="text-gray-500 text-sm">Suivez vos encaissements et décaissements</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition flex items-center justify-center gap-2"
          >
            <Wallet size={16} /> Nouveau paiement
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-green-500 text-xl md:text-2xl mb-1 md:mb-2"><Download size={24} /></div>
          <div className="text-lg md:text-xl font-bold text-green-600">{stats.totalEncaissements.toLocaleString()} <span className="text-xs">XOF</span></div>
          <div className="text-xs md:text-sm text-gray-500">Encaissements ({stats.countEncaissements})</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-red-500 text-xl md:text-2xl mb-1 md:mb-2"><Upload size={24} /></div>
          <div className="text-lg md:text-xl font-bold text-red-600">{stats.totalDecaissements.toLocaleString()} <span className="text-xs">XOF</span></div>
          <div className="text-xs md:text-sm text-gray-500">Décaissements ({stats.countDecaissements})</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4 col-span-2">
          <div className="text-indigo-500 text-xl md:text-2xl mb-1 md:mb-2"><Banknote size={24} /></div>
          <div className={`text-lg md:text-xl font-bold ${stats.totalEncaissements - stats.totalDecaissements >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {(stats.totalEncaissements - stats.totalDecaissements).toLocaleString()} <span className="text-xs">XOF</span>
          </div>
          <div className="text-xs md:text-sm text-gray-500">Solde net</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        {/* Barre de recherche */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher par client, référence, mode..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Search size={14} /></span>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {[
            { value: 'all', label: <><RotateCcw size={14} className="inline" /> Tous</> },
            { value: 'encaissement', label: <><Download size={16} className="inline" /> Encaissements</> },
            { value: 'decaissement', label: <><Upload size={16} className="inline" /> Décaissements</> }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => { setFilter(f.value); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === f.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-4 text-sm text-gray-500">
          {filteredPayments.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Payments List */}
      {paginatedPayments.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Client/Fournisseur</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Mode</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Référence</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Montant</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginatedPayments.map(payment => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(payment.date_paiement).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payment.type === 'encaissement' 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-red-100 text-red-600'
                      }`}>
                        {payment.type === 'encaissement' ? <><Download size={16} className="inline" /> Encaissement</> : <><Upload size={16} className="inline" /> Décaissement</>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{payment.client_nom || payment.fournisseur || '-'}</div>
                      {payment.numero_facture && (
                        <div className="text-xs text-gray-400">Fact. {payment.numero_facture}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {payment.mode_paiement === 'especes' && 'Espèces'}
                      {payment.mode_paiement === 'mobile_money' && 'Mobile Money'}
                      {payment.mode_paiement === 'virement' && 'Virement'}
                      {payment.mode_paiement === 'cheque' && 'Chèque'}
                      {payment.mode_paiement === 'carte' && 'Carte'}
                      {!payment.mode_paiement && '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {payment.reference || '-'}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${
                      payment.type === 'encaissement' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {payment.type === 'encaissement' ? '+' : '-'}
                      {parseFloat(payment.montant).toLocaleString()} XOF
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(payment.id)}
                        className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {paginatedPayments.map(payment => (
              <div key={payment.id} className="bg-white rounded-xl shadow p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      payment.type === 'encaissement' 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-red-100 text-red-600'
                    }`}>
                        {payment.type === 'encaissement' ? <><Download size={16} className="inline" /> Encaissement</> : <><Upload size={16} className="inline" /> Décaissement</>}
                    </span>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(payment.date_paiement).toLocaleDateString()}
                    </div>
                  </div>
                  <div className={`text-lg font-bold ${
                    payment.type === 'encaissement' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {payment.type === 'encaissement' ? '+' : '-'}
                    {parseFloat(payment.montant).toLocaleString()} XOF
                  </div>
                </div>
                <div className="flex justify-between items-center border-t pt-3">
                  <div>
                    <div className="font-medium text-gray-800 text-sm">{payment.client_nom || payment.fournisseur || '-'}</div>
                    <div className="text-xs text-gray-500">
                      {payment.mode_paiement === 'especes' && 'Espèces'}
                      {payment.mode_paiement === 'mobile_money' && 'Mobile Money'}
                      {payment.mode_paiement === 'virement' && 'Virement'}
                      {payment.mode_paiement === 'cheque' && 'Chèque'}
                      {payment.mode_paiement === 'carte' && 'Carte'}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(payment.id)}
                    className="p-2 bg-red-50 text-red-600 rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Affichage {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredPayments.length)} sur {filteredPayments.length}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  «
                </button>
                {(() => {
                  const pages = [];
                  const showPages = new Set();
                  showPages.add(1);
                  showPages.add(totalPages);
                  for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                    showPages.add(i);
                  }
                  const sortedPages = [...showPages].sort((a, b) => a - b);
                  sortedPages.forEach((page, idx) => {
                    if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                      pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                    }
                    pages.push(
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        {page}
                      </button>
                    );
                  });
                  return pages;
                })()}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block"><Wallet size={48} /></span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Aucun paiement</h3>
          <p className="text-gray-500 mb-4">Enregistrez votre premier paiement</p>
          <button
            onClick={() => setShowModal(true)}
            className="px-6 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition"
          >
            Nouveau paiement
          </button>
        </div>
      )}

      {/* Payment Modal */}
      {showModal && (
        <PaymentModal
          clients={clients}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadPayments();
          }}
          onLoadInvoices={loadInvoicesForClient}
          invoices={invoices}
          apiBase={apiBase}
        />
      )}
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ clients, invoices, onClose, onSuccess, onLoadInvoices, apiBase = '/business' }) => {
  const [form, setForm] = useState({
    type: 'encaissement',
    client_id: '',
    invoice_id: '',
    fournisseur: '',
    montant: '',
    mode_paiement: 'especes',
    reference: '',
    date_paiement: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleClientChange = (clientId) => {
    setForm({ ...form, client_id: clientId, invoice_id: '' });
    if (clientId) {
      onLoadInvoices(clientId);
    }
  };

  const handleInvoiceChange = (invoiceId) => {
    setForm({ ...form, invoice_id: invoiceId });
    if (invoiceId) {
      const invoice = invoices.find(i => String(i.id) === String(invoiceId));
      if (invoice) {
        const reste = parseFloat(invoice.total) - parseFloat(invoice.montant_paye || 0);
        setForm(f => ({ ...f, montant: reste }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`${apiBase}/payments`, form);
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Nouveau paiement</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, type: 'encaissement', fournisseur: '' })}
                className={`p-3 rounded-xl border-2 flex items-center justify-center gap-2 transition ${
                  form.type === 'encaissement'
                    ? 'border-green-500 bg-green-50 text-green-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Download size={16} /> Encaissement
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, type: 'decaissement', client_id: '', invoice_id: '' })}
                className={`p-3 rounded-xl border-2 flex items-center justify-center gap-2 transition ${
                  form.type === 'decaissement'
                    ? 'border-red-500 bg-red-50 text-red-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Upload size={16} /> Décaissement
              </button>
            </div>
          </div>

          {/* Client/Fournisseur */}
          {form.type === 'encaissement' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select
                  value={form.client_id}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.nom}</option>
                  ))}
                </select>
              </div>
              
              {form.client_id && invoices.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Facture liée (optionnel)</label>
                  <select
                    value={form.invoice_id}
                    onChange={(e) => handleInvoiceChange(e.target.value)}
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Sans facture</option>
                    {invoices.map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {inv.numero_facture} - Reste: {(parseFloat(inv.total) - parseFloat(inv.montant_paye || 0)).toLocaleString()} XOF
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur/Bénéficiaire</label>
              <input
                type="text"
                value={form.fournisseur}
                onChange={(e) => setForm({ ...form, fournisseur: e.target.value })}
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                placeholder="Nom du fournisseur"
              />
            </div>
          )}

          {/* Montant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant *</label>
            <input
              type="number"
              min="0"
              value={form.montant}
              onChange={(e) => setForm({ ...form, montant: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="0"
              required
            />
          </div>

          {/* Mode de paiement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
            <select
              value={form.mode_paiement}
              onChange={(e) => setForm({ ...form, mode_paiement: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="especes">Espèces</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="virement">Virement bancaire</option>
              <option value="cheque">Chèque</option>
              <option value="carte">Carte bancaire</option>
            </select>
          </div>

          {/* Référence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
            <input
              type="text"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="N° transaction..."
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={form.date_paiement}
              onChange={(e) => setForm({ ...form, date_paiement: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows="2"
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`flex-1 py-2 text-white rounded-xl disabled:opacity-50 ${
                form.type === 'encaissement'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {saving ? 'Enregistrement...' : 'Confirmer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessPayments;
